import React, { useEffect, useState } from 'react';
import { Loader2, TrendingUp, Award, GraduationCap } from 'lucide-react';
import {
    LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid,
    BarChart, Bar, Legend,
} from 'recharts';
import api from '../lib/api';

export default function Stats() {
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        (async () => {
            try {
                const res = await api.get('/interviews/stats');
                setStats(res.data);
            } catch (_) {
                /* ignore */
            } finally {
                setLoading(false);
            }
        })();
    }, []);

    if (loading) {
        return (
            <div className="brand-card p-10 flex items-center justify-center gap-3 text-[#6B7280]">
                <Loader2 className="h-5 w-5 animate-spin" /> Loading stats…
            </div>
        );
    }
    if (!stats || stats.total_sessions === 0) {
        return (
            <div className="brand-card p-10 text-center">
                <div className="text-lg font-bold text-[#111827]">No stats yet</div>
                <p className="mt-1 text-sm text-[#6B7280]">
                    Complete an interview to see your progress here.
                </p>
            </div>
        );
    }

    const trend = (stats.score_trend || []).map((t) => ({
        date: new Date(t.date).toLocaleDateString(),
        score: t.score,
    }));
    const byRound = Object.entries(stats.by_round || {}).map(([name, avg]) => ({ name, avg }));
    const byCourse = Object.entries(stats.by_course || {}).map(([name, count]) => ({ name, count }));

    return (
        <div className="space-y-6" data-testid="stats-page">
            <header>
                <h1 className="text-2xl md:text-3xl font-extrabold text-[#111827]">Your Statistics</h1>
                <p className="text-sm text-[#6B7280]">Track your progress across sessions and rounds.</p>
            </header>

            <div className="grid gap-4 md:grid-cols-3">
                <StatTile icon={Award} label="Total Sessions" value={stats.total_sessions} accent="#1C3A94" />
                <StatTile icon={TrendingUp} label="Average Score" value={`${stats.avg_score} / 10`} accent="#E52C2C" />
                <StatTile icon={GraduationCap} label="Courses Attempted" value={Object.keys(stats.by_course || {}).length} accent="#10B981" />
            </div>

            <section className="brand-card p-6">
                <h3 className="text-sm font-bold text-[#111827]">Score trend</h3>
                <div className="mt-4 h-72">
                    <ResponsiveContainer width="100%" height="100%">
                        <LineChart data={trend}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                            <XAxis dataKey="date" stroke="#6B7280" fontSize={11} />
                            <YAxis domain={[0, 10]} stroke="#6B7280" fontSize={11} />
                            <Tooltip />
                            <Line
                                type="monotone"
                                dataKey="score"
                                stroke="#1C3A94"
                                strokeWidth={2.5}
                                dot={{ r: 4, fill: '#E52C2C' }}
                                activeDot={{ r: 6 }}
                            />
                        </LineChart>
                    </ResponsiveContainer>
                </div>
            </section>

            <div className="grid gap-6 md:grid-cols-2">
                <section className="brand-card p-6">
                    <h3 className="text-sm font-bold text-[#111827]">Average score per round</h3>
                    <div className="mt-4 h-64">
                        <ResponsiveContainer width="100%" height="100%">
                            <BarChart data={byRound}>
                                <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                                <XAxis dataKey="name" stroke="#6B7280" fontSize={11} />
                                <YAxis domain={[0, 10]} stroke="#6B7280" fontSize={11} />
                                <Tooltip />
                                <Bar dataKey="avg" fill="#1C3A94" radius={[8, 8, 0, 0]} />
                            </BarChart>
                        </ResponsiveContainer>
                    </div>
                </section>

                <section className="brand-card p-6">
                    <h3 className="text-sm font-bold text-[#111827]">Sessions per course</h3>
                    <div className="mt-4 h-64">
                        <ResponsiveContainer width="100%" height="100%">
                            <BarChart data={byCourse}>
                                <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                                <XAxis dataKey="name" stroke="#6B7280" fontSize={11} />
                                <YAxis stroke="#6B7280" fontSize={11} />
                                <Tooltip />
                                <Legend />
                                <Bar dataKey="count" name="Sessions" fill="#E52C2C" radius={[8, 8, 0, 0]} />
                            </BarChart>
                        </ResponsiveContainer>
                    </div>
                </section>
            </div>
        </div>
    );
}

function StatTile({ icon: Icon, label, value, accent }) {
    return (
        <div className="brand-card p-5 flex items-center gap-4">
            <div
                className="flex h-11 w-11 items-center justify-center rounded-lg"
                style={{ background: `${accent}18`, color: accent }}
            >
                <Icon className="h-5 w-5" />
            </div>
            <div>
                <div className="text-xs font-medium text-[#6B7280]">{label}</div>
                <div className="text-xl font-extrabold text-[#111827]">{value}</div>
            </div>
        </div>
    );
}
